import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  legalDocumentFromPath,
  legalDocumentPathForUiLocale,
  normalizeUiLocale,
} from '../../legal/documents';

export const LanguageSwitcher: React.FC = () => {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextLanguage = e.target.value;
    i18n.changeLanguage(nextLanguage);

    // A published legal document is tied to the language in its URL, so a
    // language change there must land on the same document in the newly chosen
    // language (PT -> Portuguese, EN/ES -> English). This keeps the document
    // body, its title and the interface chrome in agreement instead of leaving
    // a mismatched body behind. Everywhere else the language changes in place.
    const kind = legalDocumentFromPath(location.pathname);
    if (kind) {
      navigate(legalDocumentPathForUiLocale(kind, normalizeUiLocale(nextLanguage)));
    }
  };

  const normalizeLanguage = (language: string | undefined) => {
    if (!language) return 'en-US';

    const normalized = language.toLowerCase();
    if (normalized.startsWith('en')) return 'en-US';
    if (normalized.startsWith('pt')) return 'pt-PT';
    if (normalized.startsWith('es')) return 'es-ES';

    return 'en-US';
  };

  const currentLanguage = normalizeLanguage(i18n.resolvedLanguage || i18n.language);

  return (
    <div className="relative">
      <select
        value={currentLanguage}
        onChange={handleChange}
        aria-label={t('common.language')}
        className="block w-full rounded-md border border-input bg-background py-1.5 pl-3 pr-8 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-ring shadow-sm cursor-pointer"
      >
        <option value="en-US">{t('common.languages.en_us')}</option>
        <option value="pt-PT">{t('common.languages.pt_pt')}</option>
        <option value="es-ES">{t('common.languages.es_es')}</option>
      </select>
    </div>
  );
};
